import { Alert, Box, Grid, InputAdornment, MenuItem, Select, SelectChangeEvent, Stack, TablePagination, TextField, Typography } from '@mui/material';
import { SearchOutlined } from '@mui/icons-material';
import { formatGroupLabel } from '../../../../shared/constants/ghg-category';
import { isSoldProductGroupId } from '../../../../shared/utils/group-ids-utils';
import { SOLD_PRODUCT_STAGE_BY_GROUP_ID } from '../../../../shared/constants/group-ids';
import { InformationBox } from '../../miscellaneous-components/information-box';
import { EmissionsInputGoods } from './emissions-input/emissions-input-goods';
import { ProductCatalogueCard } from '../../organisation-components/good/product-catalogue-card';
import { FunctionComponent, SetStateAction, useContext, useEffect, useState } from 'react';
import { IGroupDetail } from '../../../../shared/services/group/group.model';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import GoodCategoryService from '../../../../shared/services/good-category/good-category.service';
import CapitalGoodService from '../../../../shared/services/capital-good/capital-good-service';
import { ICapitalGood } from '../../../../shared/services/capital-good/capital-good.model';
import { ISubsidiary } from '../../../../shared/services/subsidiary/subsidiary.model';
import { EmissionsAddManual } from './emissions-add-manual';
import { IConversionSource } from '../../../../shared/services/conversion-source/conversion-source.model';
import { GoodCatalogueTree } from '../../organisation-components/good/good-catalogue-tree';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { IEmission } from '../../../../shared/services/emission/emission.model';
import { SupersedeResolver } from '../../../../shared/services/emission-calculation/emission-calculation.service';

export const EmissionsGoodCatalogue: FunctionComponent<{
  group: IGroupDetail,
  activeCategoryId: string,
  backgroundColor: string,
  onSave: any,
  isFormSubmitting: boolean,
  handleTransactionSubmit: any,
  showCategoryFormHandler: any,
  subsidiaries: ISubsidiary[],
  conversionSources: IConversionSource[],
  selectedEmissionFactorSourceId: string | undefined,
  emissionFactorSourceHandler: (value: IConversionSource) => void,
  setViewEmissions: React.Dispatch<SetStateAction<any>>,
  setActiveCategoryId: React.Dispatch<SetStateAction<any>>,
  setBackgroundColor: React.Dispatch<SetStateAction<any>>,
  selectedSubsidiaryId: string,
  setSelectedSubsidiaryId: React.Dispatch<React.SetStateAction<string | undefined>>,
  emission?: IEmission | null,
  resolveSupersede?: SupersedeResolver,
}> = ({
  group,
  activeCategoryId,
  backgroundColor,
  onSave,
  isFormSubmitting,
  handleTransactionSubmit,
  showCategoryFormHandler,
  subsidiaries,
  selectedEmissionFactorSourceId,
  conversionSources,
  emissionFactorSourceHandler,
  setViewEmissions,
  setActiveCategoryId,
  setBackgroundColor,
  selectedSubsidiaryId,
  setSelectedSubsidiaryId,
  emission,
  resolveSupersede,
}) => {
    //Services
    const goodCategoryService = new GoodCategoryService();
    const capitalGoodService = new CapitalGoodService()
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //State
    const [viewGood, setViewGood] = useState<string>('catalogue')
    const [hasNoGoodCategories, setHasNoGoodCategories] = useState<boolean>(false)
    const [hasNoGoods, setHasNoGoods] = useState<boolean>(false)
    const [goodCatalogue, setGoodCatalogue] = useState<any>()
    const [capitalGoodsInCategory, setCapitalGoodsInCategory] = useState<ICapitalGood[]>()
    const [selectedCatalogueItemId, setSelectedCatalogueItemId] = useState<string | null>(null);
    const [selectedGoodId, setSelectedGoodId] = useState<string>()
    const [searchTerm, setSearchTerm] = useState<string>('')
    // Pagination state
    const [inputPage, setInputPage] = useState(0)
    const [perPage, setPerPage] = useState(12)
    const [totalRecords, setTotalRecords] = useState(0)
    //Variables
    const isSoldProductGroup = isSoldProductGroupId(group.id)
    const stageConfig = SOLD_PRODUCT_STAGE_BY_GROUP_ID[group.id]
    const typeOfGood = isSoldProductGroup ? 'soldProduct'
      : group.id === 'bf3e2e90-8c4a-4fca-b9f2-2d4a1d16ab55' ? 'capitalGood'
        : group.id === '841cea5e-1c0b-4f92-a94d-d6c83ad43e5e' ? 'purchasedGood' : ''
    const addProductLink = isSoldProductGroup ? '/organisation/products/add-product?typeOfGood=soldProduct' : '/organisation/products/add-product'
    const searchParam = (term: string) => term ? `&nameIncludes=${encodeURIComponent(term)}` : ''

    useEffect(() => {
      if (typeOfGood) {
        capitalGoodService.getAllActive(`&typeOfGood=${typeOfGood}`)
          .then(response => {
            if (response.length === 0) {
              setHasNoGoods(true)
            }
          })
          .catch(err => console.log(err))

        goodCategoryService.getAllForCatalogue()
          .then(response => {
            if (response.length === 0) {
              setHasNoGoodCategories(true)
            }

            setGoodCatalogue(response)
            const defaultSelectedId = response[0].id;
            setSelectedCatalogueItemId(defaultSelectedId);
            capitalGoodService.getAllActiveWithPagination(`&typeOfGood=${typeOfGood}&goodCategory=true&category=${encodeURIComponent(defaultSelectedId)}`, 0, perPage)
              .then(response => {
                setCapitalGoodsInCategory(response.rows)
                setTotalRecords(response.count)
                setInputPage(0)
              })
              .catch(err => console.log(err))
          })
          .catch(err => console.log(err))
      }
    }, [group.id])

    const handleItemSelectionToggle = (event: React.SyntheticEvent | null, itemId: string, isSelected: boolean,) => {
      if (isSelected) {
        const parts = itemId.split('-')
        setSelectedCatalogueItemId(parts[0])
        capitalGoodService.getAllActiveWithPagination(`&typeOfGood=${typeOfGood}&goodCategory=true&category=${encodeURIComponent(parts[0])}${parts[1] ? `&subCategoryOne=${encodeURIComponent(parts[1])}${parts[2] ? `&subCategoryTwo=${encodeURIComponent(parts[2])}` : ''}` : ''}${searchParam(searchTerm)}`, 0, perPage)
          .then(response => {
            setCapitalGoodsInCategory(response.rows)
            setTotalRecords(response.count)
            setInputPage(0)
          })
          .catch(err => console.log(err))
      }
    };

    // Debounced name search (mirrors the Products page): first page of the current category.
    useEffect(() => {
      if (!goodCatalogue || !selectedCatalogueItemId) return
      const timer = setTimeout(() => {
        capitalGoodService.getAllActiveWithPagination(`&typeOfGood=${typeOfGood}&goodCategory=true&category=${encodeURIComponent(selectedCatalogueItemId)}${searchParam(searchTerm)}`, 0, perPage)
          .then(response => {
            setCapitalGoodsInCategory(response.rows)
            setTotalRecords(response.count)
            setInputPage(0)
          })
          .catch(err => console.log(err))
      }, 350)
      return () => clearTimeout(timer)
    }, [searchTerm]) // eslint-disable-line react-hooks/exhaustive-deps

    const viewGoodHandler = (e: SelectChangeEvent<string>) => {
      if (e.target.value !== null) {
        setViewGood(e.target.value)
        setSelectedGoodId('')
      }
      if (e.target.value === 'catalogue') {
        setActiveCategoryId('')
      }
    }

    const selectedGoodHandler = (goodId: string) => {
      setSelectedGoodId(goodId)
      setBackgroundColor('secondary.light');
    }

    const handleCataloguePageChange = (_: unknown, newPage: number) => {
      setInputPage(newPage)
      capitalGoodService.getAllActiveWithPagination(`${selectedCatalogueItemId ? `&typeOfGood=${typeOfGood}&goodCategory=true&category=${encodeURIComponent(selectedCatalogueItemId)}` : ''}${searchParam(searchTerm)}`, newPage, perPage)
        .then(response => {
          setCapitalGoodsInCategory(response.rows)
          setTotalRecords(response.count)
        })
        .catch(err => console.log(err))
    }

    return (
      <Box display='flex' flexDirection='row' sx={{ mt: 2 }}>
        <Grid container spacing={4}>
          <Grid item lg={3} xs={3}>
            <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 2, display: 'block' }}>{formatGroupLabel(group)}</Typography>
            {!emission && (
              <Select
                size='small'
                fullWidth
                value={viewGood}
                onChange={viewGoodHandler}
                sx={{ color: 'text.primary' }}>
                <MenuItem value={'catalogue'}>{isSoldProductGroup ? `Sold ${screenLabelsContext.getLabel('product')} catalogue` : `${screenLabelsContext.getLabel('product', { capitalize: 'first' })} Catalogue`}</MenuItem>
                <MenuItem value={'filter'}>{isSoldProductGroup ? 'Enter usage data manually' : 'Search all products'}</MenuItem>
              </Select>
            )}
            {(viewGood === 'catalogue' || emission) && goodCatalogue &&
              <Stack sx={{ mt: 4 }}>
                <GoodCatalogueTree
                  goodCatalogue={goodCatalogue}
                  handleItemSelectionToggle={handleItemSelectionToggle}
                  selectedCatalogueItemId={selectedCatalogueItemId ? selectedCatalogueItemId : ''}
                />
              </Stack>
            }
          </Grid>
          <Grid item lg={8} xs={8} >
            {(viewGood === 'catalogue' || emission) && hasNoGoodCategories &&
              <InformationBox
                icon={IconName.ErrorOutline}
                text='You do not have any product categories'
                actionText='Click here to create one'
                link={'/settings/company-settings/product-categories?openDialog=true'}
                marginTop={2}
              />
            }
            {(viewGood === 'catalogue' || emission) && !hasNoGoodCategories && hasNoGoods &&
              <InformationBox
                icon={IconName.ErrorOutline}
                text={`You do not have any ${isSoldProductGroup ? 'sold ' : ''}${screenLabelsContext.getLabel('product', { plural: true })}`}
                actionText='Click here to create one'
                link={addProductLink}
                marginTop={2}
              />
            }
            {(viewGood === 'catalogue' || emission) && !hasNoGoodCategories && !hasNoGoods &&
              <Stack direction='row' justifyContent='flex-end' sx={{ mb: 2 }}>
                <TextField
                  size='small'
                  placeholder={`Search ${isSoldProductGroup ? 'sold ' : ''}${screenLabelsContext.getLabel('product', { plural: true })}`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position='start'><SearchOutlined sx={{ color: 'text.secondary' }} /></InputAdornment>,
                  }}
                  sx={{ width: '240px' }}
                />
              </Stack>
            }
            {(viewGood === 'catalogue' || emission) && !hasNoGoodCategories && !hasNoGoods && capitalGoodsInCategory && capitalGoodsInCategory.length === 0 &&
              (searchTerm
                ? <InformationBox
                  icon={IconName.ErrorOutline}
                  text={`No ${isSoldProductGroup ? 'sold ' : ''}${screenLabelsContext.getLabel('product', { plural: true })} in this category match your search`}
                  marginTop={2}
                />
                : <InformationBox
                  icon={IconName.ErrorOutline}
                  text={`You do not have any ${isSoldProductGroup ? 'sold ' : ''}${screenLabelsContext.getLabel('product', { plural: true })} in this category`}
                  actionText='Click here to create one'
                  link={addProductLink}
                  marginTop={2}
                />)
            }
            {(viewGood === 'catalogue' || emission) && capitalGoodsInCategory && capitalGoodsInCategory.length > 0 &&
              <>
                {isSoldProductGroup && stageConfig && !emission &&
                  <Alert severity='info' sx={{ mb: 2 }}>
                    Booking from the catalogue records units sold in the period multiplied by
                    {' '}{stageConfig.bookingDescription}. If you also enter emissions manually in this group for the same
                    {' '}{screenLabelsContext.getLabel('product', { plural: true })}, they will be double counted.
                  </Alert>
                }
                < Grid container spacing={2}>
                  {capitalGoodsInCategory.map((good, index) => (
                    <Grid key={index} item lg={4} xs={6}  >
                      <Box sx={{ cursor: 'pointer' }} onClick={() => selectedGoodHandler(good.id)}>
                        <ProductCatalogueCard
                          good={good}
                          selected={selectedGoodId === good.id}
                          showSoldMarker={!isSoldProductGroup}
                          footprintLabel={isSoldProductGroup && stageConfig
                            ? (good[stageConfig.stageField] != null
                              ? `${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(good[stageConfig.stageField] as number)} kgCO₂e ${stageConfig.perUnitShort}`
                              : `No ${stageConfig.missingFigureText} figure yet`)
                            : undefined}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <TablePagination
                  rowsPerPageOptions={[perPage]}
                  component="div"
                  count={totalRecords}
                  rowsPerPage={perPage}
                  page={inputPage}
                  onPageChange={handleCataloguePageChange}
                  onRowsPerPageChange={() => { }}
                  sx={{ mt: 2 }}
                />
              </>}
            {capitalGoodsInCategory && capitalGoodsInCategory.length > 0 && selectedGoodId &&
              <Stack sx={{ mt: 4 }}>
                <EmissionsInputGoods
                  groupId={group.id}
                  onSave={onSave}
                  subsidiaries={subsidiaries}
                  selectedGoodId={selectedGoodId}
                  setViewEmissions={setViewEmissions}
                  emission={emission}
                  resolveSupersede={resolveSupersede}
                />
              </Stack>
            }
            {!emission && viewGood === 'filter' &&
              <EmissionsAddManual
                group={group}
                selectedSubsidiaryId={selectedSubsidiaryId}
                activeCategoryId={activeCategoryId}
                conversionSources={conversionSources}
                backgroundColor={backgroundColor}
                onSave={onSave}
                subsidiaries={subsidiaries}
                isFormSubmitting={isFormSubmitting}
                handleTransactionSubmit={handleTransactionSubmit}
                selectedEmissionFactorSourceId={selectedEmissionFactorSourceId}
                emissionFactorSourceHandler={emissionFactorSourceHandler}
                showCategoryFormHandler={showCategoryFormHandler}
                setSelectedSubsidiaryId={setSelectedSubsidiaryId}
              />
            }
          </Grid>
        </Grid>
      </Box>
    )
  }